import { TrackedInputRadio } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { forwardRef } from 'react';

import * as styles from './Radio.module.css';

import type { Testid } from '../../types';
import type { TrackingProps } from '../types';
import type React from 'react';

export type RadioProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'children' | 'type' | 'name' | 'value'> &
  Partial<TrackingProps> & {
    name: string; // make it required
    value: string; // make it required and only support string values
    nativeInputClassName?: string;
  } & Testid;

// https://www.figma.com/file/ssGmau7DMZsdIX7F6k0lGk/%E2%9A%A1%EF%B8%8F-Design-System?type=design&node-id=45-3610&mode=design&t=ofKIuOSPHQWXBcWo-0
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ id, className, nativeInputClassName, 'data-testid': testId, trackingId, ...props }, ref) => {
    const idWithFallback = id ?? `${props.name}-${props.value}`;
    const trackingIdWithFallback = trackingId ?? `${props.name}-${props.value}`;

    return (
      <>
        <TrackedInputRadio
          ref={ref}
          objectiv={{ id: trackingIdWithFallback }}
          className={clsx(styles.nativeRadio, nativeInputClassName)}
          id={idWithFallback}
          {...props}
        />
        <label
          htmlFor={idWithFallback}
          className={clsx(styles.customRadio, className)}
          aria-label={idWithFallback}
          data-testid={testId}
        />
      </>
    );
  },
);

// To avoid having duplicated label when it's used with other component that already has its own label like RadioCardGroup
export const RadioWithoutLabel = forwardRef<HTMLInputElement, RadioProps>(
  ({ id, className, nativeInputClassName, 'data-testid': testId, trackingId, ...props }, ref) => {
    const idWithFallback = id ?? `${props.name}-${props.value}`;
    const trackingIdWithFallback = trackingId ?? `${props.name}-${props.value}`;

    return (
      <>
        <TrackedInputRadio
          ref={ref}
          objectiv={{ id: trackingIdWithFallback }}
          className={clsx(styles.nativeRadio, nativeInputClassName)}
          id={idWithFallback}
          {...props}
        />
        <span className={clsx(styles.customRadio, className)} aria-label={idWithFallback} data-testid={testId} />
      </>
    );
  },
);
